import { AdminRole, FEATURED_EXPANSIONS_MAX } from "@repo/api-dtos"
import { createFileRoute } from "@tanstack/react-router"
import { useCallback, useMemo, useState } from "react"
import { z } from "zod"
import { PageHeader, Toolbar } from "@/components/layout"
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { FeaturedExpansionsEditor } from "@/features/featured-expansions/FeaturedExpansionsEditor"
import { GamePicker } from "@/features/featured-expansions/GamePicker"
import { useAdminRoles } from "@/hooks/use-admin-roles"
import { useGamesCoverage } from "@/queries/games/use-games-coverage"

const searchSchema = z.object({
	game: z.string().optional(),
})

export const Route = createFileRoute("/catalogue/featured-expansions/")({
	component: FeaturedExpansionsPage,
	validateSearch: searchSchema,
})

function FeaturedExpansionsPage() {
	const { game } = Route.useSearch()
	const navigate = Route.useNavigate()
	const { hasAnyRole } = useAdminRoles()
	const canWrite = hasAnyRole([
		AdminRole.CatalogueWrite,
		AdminRole.CatalogueDeploy,
	])

	const [dirty, setDirty] = useState(false)
	const [pendingGame, setPendingGame] = useState<string | null>(null)

	const coverage = useGamesCoverage()

	const counts = useMemo(() => {
		if (!coverage.data) return undefined
		return new Map(
			coverage.data.map((row) => [row.gameSlug, row.featuredExpansionsCount]),
		)
	}, [coverage.data])

	const switchGame = useCallback(
		(next: string) => {
			navigate({ search: { game: next } })
		},
		[navigate],
	)

	const handlePick = (next: string) => {
		if (next === game) return
		if (dirty) {
			setPendingGame(next)
			return
		}
		switchGame(next)
	}

	const picker = (
		<GamePicker value={game} counts={counts} onChange={handlePick} />
	)

	return (
		<div className="flex flex-col gap-6 p-6">
			<PageHeader
				title="Featured expansions"
				description={`Up to ${FEATURED_EXPANSIONS_MAX} expansions per game, in the order the game page shows them. The first slot is the hero card.`}
			/>

			{game ? (
				<FeaturedExpansionsEditor
					key={game}
					gameSlug={game}
					canWrite={canWrite}
					onDirtyChange={setDirty}
					picker={picker}
				/>
			) : (
				<>
					<Toolbar>{picker}</Toolbar>
					<p className="text-muted-foreground">
						Pick a game to edit its featured list. Games showing 0/
						{FEATURED_EXPANSIONS_MAX} fall back to their newest expansions.
					</p>
				</>
			)}

			<AlertDialog
				open={pendingGame !== null}
				onOpenChange={(open) => {
					if (!open) setPendingGame(null)
				}}
			>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>Discard unsaved changes?</AlertDialogTitle>
						<AlertDialogDescription>
							The featured list for this game has changes that are not saved.
							Switching game drops them.
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>Keep editing</AlertDialogCancel>
						<AlertDialogAction
							onClick={() => {
								if (pendingGame) switchGame(pendingGame)
								setPendingGame(null)
							}}
						>
							Discard and switch
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</div>
	)
}
